import { Headline } from '@oneaudi/unified-web-components';
import { useAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { useMoreInformationDialog } from '~/app/pages/configurator/components/more-information-dialog';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { useDebugMode } from '~/app/utils/debug-mode';
import { SeatFallbackSvg } from '~/components/image-selection-tile/components/fallback-svgs/seat-fallback-svg';
import { SelectionContainer } from '~/components/image-selection-tile/style/tile-selection-section-style';
import { TilesSelection } from '~/components/image-selection-tile/tiles-selection';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { FeatureCategory, FeatureOrPackage, SelectableOption } from '~/graphql/types';
import { state } from '~/storage';

export function Seats({ step, total }: { step: number; total: number }) {
	const { i18n } = useI18n();
	const { loadingFlags, isItemChecked, isItemLoading, changeConfiguration } = useConfigurator();
	const { openMoreInformationDialog } = useMoreInformationDialog();
	const { feature } = useVATConfig();
	const { seats } = useCatalog();
	const [activeFilters] = useAtom(state.activeFilters);
	const { config } = useMarketConfig();
	const { getName } = useDebugMode();

	const trackSelectionClickEvent = useCallback(
		(item: FeatureOrPackage, selected: boolean) => {
			const seatsFilters = activeFilters?.seats?.length > 0 ? activeFilters?.seats : 'all';

			appTrackingService.trackSelectionClickEvent({
				eventAction: EventAction.InternalLink,
				eventName: 'click to change configuration',
				label: item.name,
				elementName: 'image',
				value: `selection feature app: seats: ${seatsFilters}`,
				category: 'seats',
				elementCurrentStep: step,
				elementTotalSteps: total,
				selected,
				item,
			});
		},
		[activeFilters?.seats, step, total],
	);

	const { items } = useMemo(() => {
		if (!seats) return { items: [] };

		// Create filters according to subFamilies
		const filteredSeats = seats.filter((item) => item.__item.subFamily);

		// Map items for the tile selection
		const mappedItems = filteredSeats.map((seat) => ({
			name: getName(seat) ?? ' ',
			key: seat.featureId,
			onSelect: () => {
				trackSelectionClickEvent(seat.__item, isItemChecked(seat));
				changeConfiguration(seat as SelectableOption<FeatureCategory>);
			},
			loading: isItemLoading(seat),
			readonly: !seat.status.changeable,
			familyName: seat.__item.subFamily?.name ?? '',
			teaserText: seat.__item.texts?.intro || '',
			standard: seat.status.standard,
			imgUrl: seat.seatImageSmallUrl || '',
			selected: isItemChecked(seat),
			price: config.showPrices
				? { totalPrice: seat.totalPrice, monthlyRate: seat.monthlyRate, feature }
				: undefined,
			textLink: {
				label: i18n('ngc.actions.moreInfo'),
				onTextLinkClick: () =>
					openMoreInformationDialog(
						seat as SelectableOption<FeatureCategory>,
						seat.__item,
						() => {},
						step,
						total,
						'seats',
					),
			},
			disclaimerHeadline: seat.__item.texts?.disclaimerHeadline || undefined,
			disclaimerContent: seat.__item.texts?.disclaimerText || undefined,
			disclaimerStartDate: seat.__item.texts?.disclaimerStartDate || undefined,
			disclaimerEndDate: seat.__item.texts?.disclaimerEndDate || undefined,
		}));

		return {
			items: mappedItems,
		};
	}, [
		trackSelectionClickEvent,
		changeConfiguration,
		config.showPrices,
		getName,
		isItemChecked,
		isItemLoading,
		seats,
		feature,
	]);

	return (
		<>
			<SelectionContainer items={seats?.length}>
				<Headline className="subtitle" tag="h2" variant={{ order: '4', type: 'headline', weight: 'normal' }}>
					{i18n('ngc.equipment.seats')}
				</Headline>

				<TilesSelection
					id="seats"
					items={items}
					fallbackSVG={SeatFallbackSvg}
					isLoading={loadingFlags.checkingConflicts}
					step={step}
					totalSteps={total}
				/>
			</SelectionContainer>
		</>
	);
}
